/**
 * RootLayoutManager.js
 * Talkroute
 *
 * Created by Vel Genov on 1/15/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { Grid, Button, Dimmer, Loader } from "semantic-ui-react";
import { connect } from "react-redux";
import { Transition, CSSTransition } from "react-transition-group";
import { IconButton } from "@material-ui/core";
import { ComponentDimensions, isIOS } from "@constants/appConstants";
import { useHistory } from "react-router-dom";

import Tooltip from '@components/PresentationComponents/FormComponents/Tooltip';

import appConfig from "../config";

import routes, { routeUrlBuilders } from "../constants/routes";
import dataStateEnum from "../constants/dataStateConstants";
import UserInfoBar from "../components/UserInfoBar";
import CallStatus from "../components/CallStatus";
import InitialModal from "./InitialModal";
import AppNavigation from "./AppNavigation";
import KeypadContainer from "./KeypadContainer";
import CallHistory from "../components/CallHistory";
import TextMessages from "../components/TextMessages";
import Meetings from "../components/Meetings";
import MeetingsDetails from "../components/Meetings/Details";
import ConversationDetails from "../components/conversationComponents/ConversationDetails";
import Voicemail from "../components/Voicemail";
import VoicemailDetails from "../components/voicemailComponents/VoicemailDetails";
import uiString from "../constants/uiString";
import ipcConstants from "../constants/ipcConstants";
import MainWindowHeader from "./MainWindowHeader";
import ErrorReporter from "../components/ErrorReporter";
import InitialLoader from "./InitialLoader";
import CallComponent from "../components/CallComponent";
import PreferencesIcon from "@resources/icons/main-nav-preferences.svg";
import HistoryAndReportingIcon from "@resources/icons/graph.svg";
import CompanyPageIcon from "@resources/icons/main-nav-company-page.svg";
import SupportPageIcon from "@resources/icons/main-nav-support-page.svg";
import CrossIcon from "@resources/icons/cross-small.svg";
import ResizeIcon from "@resources/icons/resize-preferences.svg";
import EnterLeftIcon from "@resources/icons/enter-left.svg";
import EnterRightIcon from "@resources/icons/enter-right.svg";
import { domIds } from "@constants/testConstants";
import Preferences from "@components/Preferences/Preferences";
import { setOpenHistoryAndReporting, setOpenPreferences, togglePreferencesWindow } from "@actions/preferencesActions";
import { logout } from "@actions/userActions";
import HistoryAndReporting from "@components/HistoryAndReporting/HistoryAndReporting";


const middleCellSize = "300px";
const preferencesStyle = {
  position: "absolute" as any,
  zIndex: 6,
  // display: "flex",
  // top: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT,
  width: "100%",
  height: "100%",
  background: "var(--color-white)",
  padding: 0,
  transition: "opacity 300ms linear",
};

// Used to display the details view modally
const detailWidth = `calc(100% - ${middleCellSize})`;
const negativeDetailWidth = `calc(${middleCellSize} - 100%)`;
const detailsModalStyle = {
  position: "absolute",
  zIndex: "3",
  height: "100%",
  right: `${negativeDetailWidth}`,
  top: "0px",
  background: "var(--color-white)",
  width: `${detailWidth}`,
  display: "flex",
  flexDirection: "column",
  padding: "0",
  overflow: "hidden",
  visibility: "hidden",
};

// Used to display the details without modal
const detailsStyle = {
  padding: "0",
  flex: "1",
};

const mainContentWrapper = {
  display: "flex",
  minHeight: "100%",
  height: "100%",
  zIndex: 2,
  flexDirection: "column" as any,
  overflow: "hidden" as any,
};

const duration = 300;
const transitionStyles = {
  entering: { right: "0", visibility: "visible" },
  entered: { right: "0", visibility: "visible" },
  exiting: { right: `${negativeDetailWidth}`, visibility: "visible" },
  exited: { right: `${negativeDetailWidth}`, visibility: "hidden" },
};

interface RootLayoutManagerProps {
  location: {
    hash: string;
    key?: string;
    pathname: string;
    search: string;
    state: any;
  };
  callHistoryDataState: string;
  voiceMailDataState: string;
  isUserAuthenticated: boolean;
  userTokenReady: boolean;
  refreshToken?: string;
  handleClosePreferences: () => {};
  handleShowPreferences: () => {};
  handleShowHistoryAndReporting: () => {};
  handleCloseHistoryAndReporting: () => {};
  isPreferencesOpen: boolean;
  isHistoryAndReportingOpen: boolean;
  impersonateUserId: number | null;
  userEmail: string;
  logout: () => any;
  selectedConversationId?: string;
}

/**
 * The main application layout manager. Responsible for laying out different application components
 * Note: Keep application specific logic out of the layout manager
 */
class RootLayoutManager extends React.Component<RootLayoutManagerProps, any> {
  ipcRenderer = appConfig.electron?.ipcRenderer;

  /**
   * Called when the props for RootLayoutManager get updated.
   * Using this method to detect a change in location
   *
   * @param prevProps the values of the previous props
   */
  componentDidUpdate = (prevProps) => {
    const { location } = this.props;

    if (location !== prevProps.location) {
      const path = location.pathname;
      this.onRouteChanged(path);
    }
  };

  /**
   * Called when a route has changed
   * If the new path is routes.CALL_HISTORY_DETAILS, we show the sidebar
   *
   * @param path the new route path
   */
  onRouteChanged = (path) => {
    const { handleCloseHistoryAndReporting } = this.props;
    switch (path) {
      case routes.CALL_HISTORY:
        this.setState({
          activeRoute: routes.CALL_HISTORY,
          isEnterMessageDetail: false,
        });
        this.hideSidebar();
        break;
      case routes.VOICEMAIL:
        this.setState({
          activeRoute: routes.VOICEMAIL,
          isEnterMessageDetail: false,
        });
        this.hideSidebar();
        break;
      case routes.KEYPAD:
        this.setState({
          activeRoute: routes.KEYPAD,
          isEnterMessageDetail: false,
        });
        this.props.handleClosePreferences();
        this.hideSidebar();
        break;
      case routes.TEXT_MESSAGES:
        this.setState({
          activeRoute: routes.TEXT_MESSAGES,
          isEnterMessageDetail: false,
        });
        this.hideSidebar();
        break;
      case routes.CALL_HISTORY_DETAILS:
        this.setState({
          showSidebar: false,
          isSidebarModal: false,
          isEnterMessageDetail: false,
        });
        break;
      case routeUrlBuilders.OPEN_CONVERSATION(this.props.selectedConversationId || '', true): 
        handleCloseHistoryAndReporting();
        this.setState({
          showSidebar: false,
          isSidebarModal: true,
          isEnterMessageDetail: true,
        });
        break;
      case routeUrlBuilders.OPEN_CONVERSATION(this.props.selectedConversationId || '', false):
        handleCloseHistoryAndReporting();
        this.setState({
          showSidebar: false,
          isSidebarModal: false,
          isEnterMessageDetail: false,
        });
        break;
      case routes.VOICEMAIL_DETAILS:
        this.setState({
          showSidebar: true,
          isSidebarModal: false,
          isEnterMessageDetail: false,
        });
        break;
      case routes.MEETINGS:
        this.setState({
          activeRoute: routes.MEETINGS,
          showSidebar: true,
          isSidebarModal: false,
          isEnterMessageDetail: false,
        });
        break;
        case routes.CALLER_ID:
          this.props.handleShowPreferences();
          break;
        case routes.TEXT_MESSAGES:
          this.props.handleShowPreferences();
          break;
        case routes.CONTROLS:
          this.props.handleShowPreferences();
          break;
        case routes.NOTIFICATIONS_AND_SOUNDS:
          this.props.handleShowPreferences();
          break;
        case routes.CALL_HISTORY_AND_REPORTING:
          this.props.handleShowHistoryAndReporting();
          break;
        case routes.TEXT_MESSAGE_HISTORY:
          this.props.handleShowHistoryAndReporting();
          break;
        case routes.FORWARDING_PHONE_NUMBERS:
          this.props.handleShowPreferences();
          break;
        case routes.TRANSFER_CODES:
          this.props.handleShowPreferences();
          break;
        case routes.CONTACTS:
            this.props.handleShowPreferences();
        break;
        case routes.HOURS_OF_OPERATION:
          this.props.handleShowPreferences();
          break;
        case routes.GREETINGS_AND_MUSIC_ON_HOLD:
          this.props.handleShowPreferences();
          break;
        case routes.PREFERENCES_VOICEMAIL:
          this.props.handleShowPreferences();
          break;
        case routes.BLOCKED_NUMBERS:
          this.props.handleShowPreferences();
        break;
      default:
        this.hideSidebar();
    }
  };

  /**
   * Hides the sidebar
   */
  hideSidebar = () =>
    this.setState({ showSidebar: false, isSidebarModal: false, isEnterMessageDetail: false });

  /**
   * Determines if we need to show the right grid column as a sidebar
   * This function will get updates as we have different elements to show in the sidebar
   *
   * @param showSidebar a boolean variable to tell the function if the sidebar should be shown
   * @param isModal show the details colum be shown modally
   */
  showDetailsColumn = (props) => {
    const { showSidebar, isModal } = props;
    const style = isModal ? detailsModalStyle : detailsStyle;
    const { isEnterMessageDetail } = this.state;

    if (showSidebar) {
      return (
        <Grid.Column stretched style={style}>
          <Switch>
            <Route
              path={routes.VOICEMAIL_DETAILS}
              component={VoicemailDetails}
            />
            <Route
              path={routes.MEETINGS}
              component={MeetingsDetails}
            />
          </Switch>
        </Grid.Column>
      );
    }

    // If the route is not History Details, Voicemail Details page, always mounts Conversation Detail sidebar.
    return (
      <Transition in={isEnterMessageDetail} timeout={duration}>
        {(state) => (
          <div
            style={{
              ...detailsModalStyle,
              ...transitionStyles[state],
              transition: `right ${duration}ms ease-out`,
            }}
          >
            <ConversationDetails />
          </div>
        )}
      </Transition>
    );
  };

  // /**
  //  * Handles showing the preferences
  //  */
  // handleShowPreferences = () => {

  //   this.setState({ openPreferences: true });

  // };

  // /**
  //  * Handles closing of the preferences menu
  //  */
  // handleClosePreferences = () => {
  //   // this.nav.disableMenu(false);
  //   this.setState({ openPreferences: false });

  // };

  /**
   * Handles resizing of the preferences main content
   */
  handleResizePreferences = () => {
    this.setState({ fullSizePreferences: !this.state.fullSizePreferences });
  };

 /**
 * Handles resizing of the history and reporting main content
 */
  handleResizeHistoryAndReporting = () => {
    this.setState({ fullSizeHistoryAndReporting: !this.state.fullSizeHistoryAndReporting });
  };

  handleLogout = () => {
    const { logout: logoutLocal } = this.props;
    logoutLocal();
  }

  /**
   * Displays the content in the main screen
   */
  showMainContent = () => {
    const { impersonateUserId } = this.props;
    const { userEmail } = this.props;
    
    return (
      <div style={{ ...mainContentWrapper }}>
        <MainWindowHeader />
        <ErrorReporter />
        <Switch>
          <Route
            path={routes.MEETINGS}
            render={() => impersonateUserId ? (
                <Redirect to={routes.KEYPAD} />
              ) : (
                <Meetings />
              )
            }
          />
          <Route path={routes.KEYPAD} component={KeypadContainer} />
          <Route path={routes.CALL_HISTORY} component={CallHistory} />
          <Route path={routes.TEXT_MESSAGES} exact component={TextMessages} />
          <Route path={routes.TEXT_MESSAGE_SINGLE} component={TextMessages} /> {/* TEXT_MESSAGE_SINGLE has :id (conversation ID) as a route param, we use it to call selectConversation() in <TextMessages /> */}
          <Route path={routes.VOICEMAIL} component={Voicemail} />
          <Route path={routes.CALLER_ID} component={KeypadContainer} /> {/* route to keypad as it is the default page */}
          <Route path={routes.CONTROLS} component={KeypadContainer} /> {/* this way we will get the same behaviour as the preferences modal */}
          <Route path={routes.CONTACTS} component={KeypadContainer} /> {/* this way we will get the same behaviour as the preferences modal */}
          <Route path={routes.NOTIFICATIONS_AND_SOUNDS} component={KeypadContainer} />
          <Route path={routes.CALL_HISTORY_AND_REPORTING} component={KeypadContainer} />
          <Route path={routes.FORWARDING_PHONE_NUMBERS} component={KeypadContainer} />
          <Route path={routes.TRANSFER_CODES} component={KeypadContainer} />
          <Route path={routes.HOURS_OF_OPERATION} component={KeypadContainer} />
          <Route path={routes.GREETINGS_AND_MUSIC_ON_HOLD} component={KeypadContainer} />
          <Route path={routes.PREFERENCES_VOICEMAIL} component={KeypadContainer} />
          <Route path={routes.BLOCKED_NUMBERS} component={KeypadContainer} />
        </Switch>
      </div>
    );
  };

  /**
   * Displays a loading
   */
  showLoading = () => (
    <Dimmer active style={{ height: "100%", zIndex: "5" }}>
      <Loader />
    </Dimmer>
  );

  /**
   * Draw the general layout of the main screen, and call the appropriate methods to populate the content
   */
  showMainLayout = () => {
    const { showSidebar, isSidebarModal, activeRoute } = this.state;
    const { callHistoryDataState, voiceMailDataState } = this.props;

    // Shrink the middle column only if we are showing the sidebar and it's not modal
    const middleCellClass =
      showSidebar && !isSidebarModal ? "middle-cell" : "middle-cell-full";

    if (
      activeRoute === routes.CALL_HISTORY &&
      callHistoryDataState === dataStateEnum.LOADING
    )
      return this.showLoading();

    if (
      activeRoute === routes.VOICEMAIL &&
      voiceMailDataState === dataStateEnum.LOADING
    )
      return this.showLoading();

    return (
      <CSSTransition
        in
        appear
        key={activeRoute}
        timeout={300}
        classNames="fade"
      >
        <Grid style={{ height: "100%", margin: "0px" }}>
          {/* The right or middle column depending on the visibility of the right sidebar */}
          {/* The column width is determined dynamically based on the sidebar size */}
          {/* If statement set to hide Meetings Route */}
          <Grid.Column
            stretched
            className={middleCellClass}
            style={{ padding: "0px" }}
          >
            {this.showMainContent()}
          </Grid.Column>
          {/* Show the deteils column if necessary */}
          {this.showDetailsColumn({ showSidebar, isModal: isSidebarModal })}
        </Grid>
      </CSSTransition>
    );
  };

 

  /**
   * Draw the company page button
   */
  showCompanyPageButton = () => (
    <Tooltip title={uiString.tooltips.CONTROL_PANEL_BUTTON}>
      <IconButton
        id={domIds.COMPANY_PAGE_BUTTON}
        onClick={() => {
          window.open(uiString.COMPANY_PAGE_LINK, '_blank')
        }}
        style={{
          boxShadow: "none",
          fontSize: "1em",
          position: "absolute",
          left: "7em",
          bottom: "1em",
          padding: 11,
        }}
      >
        <CompanyPageIcon />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the support page button
   */
  showSupportPageButton = () => (
    <Tooltip title={uiString.tooltips.SUPPORT_BUTTON} >
      <IconButton
        id={domIds.SUPPORT_PAGE_BUTTON}
        onClick={() => {
          window.open(uiString.SUPPORT_PAGE_LINK, '_blank')
        }}
        style={{
          boxShadow: "none",
          fontSize: "1em",
          position: "absolute",
          left: "10em",
          bottom: "1em",
          padding: 11,
        }}
      >
        <SupportPageIcon />
      </IconButton>
    </Tooltip>
  );

  constructor(props) {
    super(props);
    this.state = {
      // Controls the visibility of the sidebar
      showSidebar: false,
      // Should the sidebar be modal
      isSidebarModal: false,
      // Controls the visibility of the preferences screen
      openPreferences: props.isPreferencesOpen,
      // Controls the visibility of the history and reporting screen
      openHistoryAndReporting: props.isHistoryAndReportingOpen,
      // The pathname of current page
      activeRoute: routes.KEYPAD,
      // If it is true, enters in animation Message Detail section
      isEnterMessageDetail: false,
      fullSizePreferences: false,
      fullSizeHistoryAndReporting: false,
    };
  }

  componentDidMount() {
    // Listen for the ipcConstants.SHOWPREFERENCES message
    if (this.ipcRenderer)
      this.ipcRenderer.on(
        ipcConstants.SHOW_PREFERENCES,
        this.props.handleShowPreferences
    );
    
    const { location } = this.props;
    const path = location.pathname;
    this.onRouteChanged(path);
    if(path) {
      this.showPreferences(true);
    }
  }

  componentWillUnmount() {
    // It's important to remove the listener when components get unmounted. Memory leaks otherwise
    if (this.ipcRenderer)
      this.ipcRenderer.removeListener(
        ipcConstants.SHOW_PREFERENCES,
        this.props.handleShowPreferences
      );
  }

  /**
   * Shows the preferences
   */
showPreferences = (isOpen: boolean) => {

  let WebkitAppRegion = "inherit";
  if (isIOS) WebkitAppRegion = "drag";

  return (
    <CSSTransition
      key="preferences"
      in={this.props.isPreferencesOpen}
      appear
      unmountOnExit
      mountOnEnter
      timeout={300}
      classNames="fade"
    >
      <div style={preferencesStyle}>
        <div
          className="text-normal none-select-text"
          style={
            {
              textAlign: "center",
              height: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT,
              display: "flex",
              justifyContent: "center",
              alignItems:"center",
              borderBottom: "1px solid var(--color-dark-grey)",
              fontSize: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_FONT_SIZE,
              position: "fixed",
              background: "white",
              zIndex: 4,
              width: "100vw",
              maxHeight:"100vh",
              WebkitAppRegion,
            } as any
          }
        >
          <div style={{margin: "auto"}}>
            {uiString.PREFERENCES_WINDOW_TITLE}
          </div>
          <this.ResizePreferencesButton handleResizePreferences={this.handleResizePreferences} fullSizePreferences={this.state.fullSizePreferences} />
          <this.ClosePreferencesButton handleClosePreferences={this.props.handleClosePreferences}/>
        </div>
        <Preferences openPreferences={this.props.isPreferencesOpen} fullSizePreferences = {this.state.fullSizePreferences} />
      </div>
    </CSSTransition>
  );
};

ShowPreferenceButton = ({ handleShowPreferences }) => {
  
  return(
    <Tooltip title={uiString.tooltips.SETTINGS_BUTTON}>
      <IconButton
        id={domIds.SHOW_PREFERENCES_BUTTON}
        onClick={() => {
          handleShowPreferences();
          }
        }
        style={{
          boxShadow: "none",
          fontSize: "1em",
          position: "absolute",
          left: "1em",
          bottom: "1em",
          padding: 11,
        }}
      >
        <PreferencesIcon />
      </IconButton>
    </Tooltip>
    )
};

  /**
   * Shows History & Reporting
   */
  showHistoryAndReporting = (isOpen: boolean) => {

    let WebkitAppRegion = "inherit";
    if (isIOS) WebkitAppRegion = "drag";
  
    return (
      <CSSTransition
        key="history-and-reporting"
        in={this.props.isHistoryAndReportingOpen}
        appear
        unmountOnExit
        mountOnEnter
        timeout={300}
        classNames="fade"
      >
        <div style={preferencesStyle}>
          <div
            className="text-normal none-select-text"
            style={
              {
                textAlign: "center",
                height: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT,
                display: "flex",
                justifyContent: "center",
                alignItems:"center",
                borderBottom: "1px solid var(--color-dark-grey)",
                fontSize: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_FONT_SIZE,
                position: "fixed",
                background: "white",
                zIndex: 4,
                width: "100vw",
                maxHeight:"100vh",
                WebkitAppRegion,
              } as any
            }
          >
            <div style={{margin: "auto"}}>
              {uiString.HISTORY_AND_REPORTING_WINDOW_TITLE}
            </div>
            <this.ResizeHistoryAndReportingButton handleResizeHistoryAndReporting={this.handleResizeHistoryAndReporting} fullSizeHistoryAndReporting={this.state.fullSizeHistoryAndReporting} />
            <this.CloseHistoryAndReportingButton handleCloseHistoryAndReporting={this.props.handleCloseHistoryAndReporting}/>
          </div>
          <HistoryAndReporting openHistoryAndReporting={this.props.isHistoryAndReportingOpen} fullSizeHistoryAndReporting = {this.state.fullSizeHistoryAndReporting} />
        </div>
      </CSSTransition>
    );
  };

ShowHistoryAndReportingButton = ({ handleShowHistoryAndReporting }) => {
  
  return(
    <Tooltip title={uiString.tooltips.HISTORY_AND_REPORTING_BUTTON}>
      <IconButton
        id={domIds.SHOW_HISTORY_REPORTING_BUTTON}
        onClick={() => {
            handleShowHistoryAndReporting();
          }
        }
        style={{
          boxShadow: "none",
          fontSize: "1em",
          position: "absolute",
          left: "4em",
          bottom: "1em",
          padding: 11,
        }}
      >
        <HistoryAndReportingIcon />
      </IconButton>
    </Tooltip>
    )
};

ClosePreferencesButton = ({ handleClosePreferences }) => {

  const history = useHistory();

  return(
    <div
      style={{
        height: "60px",
        transform: "translateX(-20px)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Button
        id={domIds.CLOSE_PREFERENCES_BUTTON}
        basic
        icon
        compact
        onClick={() => {
          handleClosePreferences();
          history.push(`${routes.KEYPAD}`);
          }
        }
        style={{
          boxShadow: "none",
          padding: "0",
        }}
        className="cross-button"
      >
        <CrossIcon style={{
          width: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE,
          height: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE
        }} />
      </Button>
    </div>
  );
}

ResizePreferencesButton = ({ handleResizePreferences, fullSizePreferences }) => {
  const IconComponent = fullSizePreferences ? EnterRightIcon : EnterLeftIcon;
  return(
    <div
      style={{
        height: "60px",
        transform: "translateX(-20px)",
        display: "flex",
        alignItems: "center",
        marginRight: "10px"
      }}
    >
      <Button
        id={domIds.RESIZE_PREFERENCES_BUTTON}
        basic
        icon
        compact
        onClick={() => handleResizePreferences()}
        style={{
          boxShadow: "none",
          padding: "0",
        }}
        className="cross-button"
      >
        <IconComponent style={{
          width: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE,
          height: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE
        }} />
      </Button>
    </div>
  );
}

CloseHistoryAndReportingButton = ({ handleCloseHistoryAndReporting }) => {

  const history = useHistory();

  return(
    <div
      style={{
        height: "60px",
        transform: "translateX(-20px)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Button
        id={domIds.CLOSE_HISTORY_REPORTING_BUTTON}
        basic
        icon
        compact
        onClick={() => {
          handleCloseHistoryAndReporting();
          history.push(`${routes.KEYPAD}`);
          }
        }
        style={{
          boxShadow: "none",
          padding: "0",
        }}
        className="cross-button"
      >
        <CrossIcon style={{
          width: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE,
          height: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE
        }} />
      </Button>
    </div>
  );
}

ResizeHistoryAndReportingButton = ({ handleResizeHistoryAndReporting, fullSizeHistoryAndReporting }) => {
  const IconComponent = fullSizeHistoryAndReporting ? EnterRightIcon : EnterLeftIcon;

  return(
    <div
      style={{
        height: "60px",
        transform: "translateX(-20px)",
        display: "flex",
        alignItems: "center",
        marginRight: "10px"
      }}
    >
      <Button
        id={domIds.RESIZE_HISTORY_REPORTING_BUTTON}
        basic
        icon
        compact
        onClick={() => handleResizeHistoryAndReporting()}
        style={{
          boxShadow: "none",
          padding: "0",
        }}
        className="cross-button"
      >
        <IconComponent style={{
          width: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE,
          height: ComponentDimensions.SETTINGS_AND_PREFERENCES_CLOSE_ICON_SIZE
        }} />
      </Button>
    </div>
  );
}

ImpersonationBanner = ({ impersonationId, impersonationEmail, handleLogout }: { impersonationId: number; impersonationEmail: string; handleLogout: () => void }) => {
  return(
    <div
      style={{
        display: "flex",
        position: "sticky",
        top: 0,
        backgroundColor: "var(--color-navy-blue)",
        width: "100%",
        color: "var(--color-white)",
        padding: "7px 20px",
        zIndex: 9999
      }}
    >
      <div
        style={{
          display: "flex",
          width: "50%"
        }}
      >
        <span>{ uiString.IMPERSONATION.NOTIFY + impersonationEmail }</span>
      </div>
      <div
        style={{
          display: "flex",
          width: "50%",
          justifyContent: "flex-end"
        }}
      >
        <span
          style={{
            textDecoration: "underline",
            fontWeight: "bold",
            cursor: "pointer"
          }}
          onClick={() => { 
            const result = handleLogout();
            handleLogout();
          }}
        >{ uiString.IMPERSONATION.EXIT }</span>
      </div>
    </div>
  );
}

  render() {
    const { isUserAuthenticated, userTokenReady, impersonateUserId, userEmail } = this.props;

    return (  isUserAuthenticated && userTokenReady ?
      <Grid padded style={{ height: "100%", position: "relative" }}>
        {impersonateUserId ? <this.ImpersonationBanner impersonationId={impersonateUserId} impersonationEmail={userEmail} handleLogout={this.handleLogout}/> : null}
        <InitialModal />
        <InitialLoader />
        <CallComponent />
        {this.showPreferences(this.props.isPreferencesOpen)}
        {this.showHistoryAndReporting(this.props.isHistoryAndReportingOpen)}
        {/* The left column, containing user options and the nav menu, preferences button */}
        <Grid.Column stretched className="navigation-cell">
          <Grid.Row stretched>
            <UserInfoBar />
            <div style={{ height: "78px", padding: "10px 0" }}>
              <CallStatus />
            </div>
            <AppNavigation />
          </Grid.Row>
          <Grid.Row style={{ fontSize: "18px", position: "relative" }}>
            <this.ShowPreferenceButton handleShowPreferences={this.props.handleShowPreferences}/>
            <this.ShowHistoryAndReportingButton handleShowHistoryAndReporting={this.props.handleShowHistoryAndReporting}/>
            {this.showCompanyPageButton()}
            {this.showSupportPageButton()}
          </Grid.Row>
        </Grid.Column>
        <Grid.Column style={{ padding: "0px", flex: "1", height: "100%", overflow: 'hidden'}}>
          {/* This column contains the main content of the application */}
          {this.showMainLayout()}
        </Grid.Column>
      </Grid> : <></>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  callHistoryDataState: state.callHistory.dataState,
  voiceMailDataState: state.voicemails.dataState,
  isUserAuthenticated: state.authentication.isUserAuthenticated,
  userTokenReady: state.authentication.userTokenReady,
  refreshToken: state.authentication.refreshToken,
  // Temporary hides the Meetings Route
  hideMeetingsRoute: false,
  isPreferencesOpen: state.preferences.openPreferences,
  isHistoryAndReportingOpen: state.preferences.openHistoryAndReporting,
  impersonateUserId: state.impersonateUserReducer.impersonateUserId,
  userEmail: state.authentication.userName,
  selectedConversationId: state.conversations.selectedConversationId,
});

const mapDispatchToProps = (dispatch) => {
  return {
      handleShowPreferences: () => dispatch(setOpenPreferences(true)),
      handleShowHistoryAndReporting: () => dispatch(setOpenHistoryAndReporting(true)),
      handleClosePreferences: () => dispatch(setOpenPreferences(false)),
      handleCloseHistoryAndReporting: () => dispatch(setOpenHistoryAndReporting(false)),
      logout: () => dispatch(logout()),
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(RootLayoutManager);



